import { NextActionValue } from '../components/NextActionMark'
import { LeaseStatus } from '../../../models/Lease/types'
import { OccupancyLease } from './types'

export type OccupancyLeaseType = 'renewal' | 'transfer' | 'new_move_in'

export function getLeaseType(lease: OccupancyLease): OccupancyLeaseType {
    if (lease.renewal_offer?.status === 'transfer_requested') return 'transfer'
    if (lease.is_renewal || lease.renewal_offer?.kind === 'renewal') {
        return 'renewal'
    }
    return 'new_move_in'
}

export function getLeaseTypeLabel(type: OccupancyLeaseType): string {
    switch (type) {
        case 'renewal':
            return 'Renewal'
        case 'transfer':
            return 'Transfer'
        case 'new_move_in':
            return 'New Move-in'
    }
}

export function isOccupancyImmovable(lease: OccupancyLease) {
    return (
        Boolean(lease.is_renewal) && lease.occupancy_assignment === 'confirmed'
    )
}

export function isUnassignedOccupancyLease(lease: OccupancyLease) {
    return lease.area_id == null && lease.status === LeaseStatus.FUTURE
}

export function resolveOccupancyNextAction(
    lease: OccupancyLease,
): NextActionValue | null {
    if (!lease.area_id || lease.occupancy_assignment === 'unassigned') {
        return { waiting_on: 'us', label: 'Assign a room' }
    }
    if (lease.occupancy_assignment === 'suggested') {
        return { waiting_on: 'us', label: 'Confirm room' }
    }
    if (!lease.lifestyle_complete) {
        return { waiting_on: 'them', label: 'Complete roommate profile' }
    }
    return null
}

export function placementSourceLabel(lease: OccupancyLease) {
    if (lease.area_id == null) return null
    const type = getLeaseType(lease)
    if (type === 'renewal') return 'Renewal staying'
    if (type === 'transfer' && lease.occupancy_assignment !== 'suggested') {
        return 'Transfer'
    }
    if (lease.occupancy_source === 'matcher') {
        return lease.occupancy_assignment === 'suggested'
            ? 'Suggested by matcher'
            : 'Placed by matcher'
    }
    if (lease.occupancy_source === 'manual') return 'Placed manually'
    if (lease.occupancy_assignment === 'suggested') return 'Suggested'
    return null
}

export function unitPlacementCaption(occupants: Array<OccupancyLease | null>) {
    const people = occupants.filter((row): row is OccupancyLease => row != null)
    let renewals = 0
    let transfers = 0
    let matcher = 0
    let manual = 0
    for (const lease of people) {
        const type = getLeaseType(lease)
        if (type === 'renewal') renewals += 1
        else if (
            type === 'transfer' &&
            lease.occupancy_assignment !== 'suggested'
        ) {
            transfers += 1
        } else if (lease.occupancy_source === 'matcher') matcher += 1
        else if (lease.occupancy_source === 'manual') manual += 1
    }
    const parts: string[] = []
    if (renewals) {
        parts.push(`${renewals} renewal${renewals === 1 ? '' : 's'} staying`)
    }
    if (transfers) {
        parts.push(`${transfers} transfer${transfers === 1 ? '' : 's'}`)
    }
    if (matcher) {
        parts.push(
            `${matcher} ${matcher === 1 ? 'person' : 'people'} by matcher`,
        )
    }
    if (manual) {
        parts.push(`${manual} placed manually`)
    }
    return parts.join(' · ')
}

export function unitIsChanged(
    occupants: Array<OccupancyLease | null>,
    bedCount: number,
) {
    const people = occupants.filter((row): row is OccupancyLease => row != null)
    if (!people.length || people.length >= bedCount) return false
    if (people.some((row) => row.occupancy_assignment === 'suggested')) {
        return false
    }
    if (!people.every((row) => row.occupancy_assignment === 'confirmed')) {
        return false
    }
    return people.some((row) => getLeaseType(row) !== 'renewal')
}
